import type { PermissionLevelValue } from '@crm/shared';
import { Badge, Card, CardContent, CardDescription, CardHeader, CardTitle, Spinner } from '@crm/ui';
import { ShieldCheck } from 'lucide-react';
import { PageHeader } from '@/components';
import { useDocumentTitle } from '@/hooks';
import { usePermissionsCatalog, useSession } from '@/modules/auth/hooks';
import { toErrorMessage } from '@/modules/auth/utils';

const LEVEL_VARIANT: Record<PermissionLevelValue, 'outline' | 'warning' | 'success'> = {
  NONE: 'outline',
  READ: 'warning',
  WRITE: 'success',
};

function LevelBadge({ level, label }: { level: PermissionLevelValue; label: string }) {
  return <Badge variant={LEVEL_VARIANT[level]}>{label}</Badge>;
}

export function PermissionsPage() {
  useDocumentTitle('Permissoes');

  const { user } = useSession();
  const catalog = usePermissionsCatalog();

  if (catalog.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner className="h-6 w-6 text-muted-foreground" />
      </div>
    );
  }

  if (catalog.isError || !catalog.data) {
    return <p className="text-sm text-destructive">{toErrorMessage(catalog.error)}</p>;
  }

  const { modules, levels, profiles, effective } = catalog.data;
  const levelLabel = (level: PermissionLevelValue) =>
    levels.find((entry) => entry.key === level)?.label ?? level;

  return (
    <div className="space-y-6">
      <PageHeader
        icon={<ShieldCheck />}
        title="Permissoes"
        description="Consulta e escrita por modulo, conforme o perfil de acesso."
      />

      <Card variant="flush">
        <CardHeader>
          <CardTitle className="text-base">Seu acesso</CardTitle>
          <CardDescription>
            {user
              ? `A soma dos seus perfis: ${user.profiles.map((profile) => profile.name).join(', ') || 'nenhum perfil atribuido'}.`
              : null}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-4">
            {modules.map((module) => (
              <div
                key={module.key}
                className="flex items-center justify-between gap-2 rounded-md border border-border px-3 py-2"
              >
                <span className="text-sm font-medium">{module.label}</span>
                <LevelBadge
                  level={effective[module.key]}
                  label={levelLabel(effective[module.key])}
                />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card variant="flush">
        <CardHeader>
          <CardTitle className="text-base">Matriz por perfil</CardTitle>
          <CardDescription>
            Cada usuario pode ter varios perfis; o acesso dele e a soma do que os perfis permitem.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Uma matriz nao cabe no celular: vira um bloco por modulo,
              com o nivel de cada perfil embaixo do nome dele. */}
          <ul className="space-y-4 md:hidden">
            {modules.map((module) => (
              <li key={module.key} className="rounded-md border border-border p-3">
                <p className="mb-2 font-medium">{module.label}</p>
                <ul className="space-y-2">
                  {profiles.map((profile) => (
                    <li key={profile.id} className="flex items-center justify-between gap-3">
                      <span className="text-sm text-muted-foreground">{profile.name}</span>
                      <LevelBadge
                        level={profile.permissions[module.key]}
                        label={levelLabel(profile.permissions[module.key])}
                      />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>

          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-160 text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Modulo</th>
                  {profiles.map((profile) => (
                    <th key={profile.id} className="pb-2 pr-4 font-medium">
                      {profile.name}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modules.map((module) => (
                  <tr key={module.key} className="border-b border-border/50 last:border-0">
                    <td className="py-2 pr-4 font-medium">{module.label}</td>
                    {profiles.map((profile) => (
                      <td key={profile.id} className="py-2 pr-4">
                        <LevelBadge
                          level={profile.permissions[module.key]}
                          label={levelLabel(profile.permissions[module.key])}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
